import { useContext, useEffect, useRef, useState } from 'react';
import { isMobileDevice } from '@repo-level/core-front';
import { AddonsContext } from 'contexts/addons';
import { useCurrentRoute } from 'react-navi';

type ScrollCoordinates = {
  x: number;
  y: number;
};

export function useScrollCoordinates() {
  const [scrollCoordinates, setScrollCoordinates] = useState<ScrollCoordinates>(
    { x: 0, y: 0 }
  );

  useEffect(() => {
    const fn = () => {
      setScrollCoordinates({ x: window.scrollX, y: window.scrollY });
    };

    document.addEventListener('scroll', fn);

    return () => {
      document.removeEventListener('scroll', fn);
    };
  }, []);

  return scrollCoordinates;
}

export function useHorizontalScroll() {
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!isMobileDevice) {
      const el = ref.current;

      if (el) {
        const onWheel = (e: WheelEvent) => {
          if (+e.deltaY === 0) return;

          e.preventDefault();
          el.scrollTo({
            left: el.scrollLeft + e.deltaY,
          });
        };
        el.addEventListener('wheel', onWheel, false);
        return () => el.removeEventListener('wheel', onWheel, false);
      }
    }
  }, []);

  return ref;
}

const EMAIL_CONFIRMATION_POPUP_HEIGHT = 34;

export function useScrollToElement() {
  const { isEmailConfirmationPopup } = useContext(AddonsContext);
  const {
    url: { hash },
  } = useCurrentRoute();

  useEffect(() => {
    if (!hash) return;

    const scrollToElement = () => {
      const el = document.querySelector(
        `[data-scroll-id="${hash.replace('#', '')}"]`
      );
      if (!el) return;

      const rect = el.getBoundingClientRect();
      const y = rect.top + window.scrollY;

      const top = isEmailConfirmationPopup
        ? y - EMAIL_CONFIRMATION_POPUP_HEIGHT
        : y;

      window.scrollTo({ top });
    };

    const timeoutId = setTimeout(scrollToElement, 0);

    return () => clearTimeout(timeoutId);
  }, [hash, isEmailConfirmationPopup]);
}

export function useScrollToId() {
  return (id: string, offset = 0) => {
    const element = document.getElementById(id);
    if (element) {
      const top =
        element.getBoundingClientRect().top + window.pageYOffset - offset;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };
}
